import React, { useMemo, useRef, useEffect, useCallback } from 'react'

/**
 * WordLevelLine - Renders a single lyric line with per-word karaoke-style highlighting
 *
 * Words light up with a sweeping left-to-right animation as they are sung.
 * Uses requestAnimationFrame for smooth 120Hz animation with direct DOM updates.
 */
const WordLevelLine = React.memo(({
  segments,
  translation,
  romanization,
  currentTime,
  isCurrent,
  isPast,
  isPlaying = false,
  showLyrics = true,
  showTranslation = true,
  showRomanization = true,
  onClick,
  fontSize = '40px'
}) => {
  // Refs for smooth time interpolation
  const lastTimeRef = useRef(currentTime)
  const lastUpdateRef = useRef(performance.now())
  const rafIdRef = useRef(null)
  const wordRefsRef = useRef([])
  const lastInterpolatedTimeRef = useRef(currentTime)

  // Update refs when currentTime prop changes (from audio callback)
  useEffect(() => {
    lastTimeRef.current = currentTime
    lastUpdateRef.current = performance.now()
    // When we get a real update from the engine, we should sync our local interpolation
    lastInterpolatedTimeRef.current = currentTime
  }, [currentTime])

  // CRITICAL: When playback resumes, we MUST reset the lastUpdateRef.
  // Otherwise, the next animation frame will think minutes have passed
  // and jump the animation to the end of the line instantly.
  useEffect(() => {
    if (isPlaying) {
      lastUpdateRef.current = performance.now()
    }
  }, [isPlaying])

  // Calculate word state for a given time
  const calculateWordState = useCallback((word, time) => {
    const actualStart = word.start_time
    const actualEnd = word.end_time
    const duration = actualEnd - actualStart

    if (time >= actualEnd) {
      return { state: 'sung', progress: 100, glowOpacity: 0 }
    }
    if (time >= actualStart) {
      const elapsed = time - actualStart
      const progress = Math.min(100, Math.max(0, (elapsed / duration) * 100))
      const glowOpacity = Math.min(0.4, (elapsed / 0.18) * 0.4)
      return { state: 'active', progress, glowOpacity }
    }
    return { state: 'upcoming', progress: 0, glowOpacity: 0 }
  }, [])

  // High-frequency animation loop - updates DOM directly for smooth 120Hz
  useEffect(() => {
    if (!isCurrent || !segments || segments.length === 0) {
      return
    }

    let isRunning = true

    const animate = () => {
      if (!isRunning) return

      let interpolatedTime
      if (isPlaying) {
        const now = performance.now()
        const elapsed = (now - lastUpdateRef.current) / 1000
        interpolatedTime = lastTimeRef.current + elapsed
        lastInterpolatedTimeRef.current = interpolatedTime
      } else {
        // When paused, use the exact time we stopped at (including sub-tick interpolation)
        interpolatedTime = lastInterpolatedTimeRef.current
      }

      // Update each word's visual state directly in DOM
      segments.forEach((word, idx) => {
        const el = wordRefsRef.current[idx]
        if (!el) return

        const { state, progress, glowOpacity } = calculateWordState(word, interpolatedTime)

        // Update classes
        el.classList.remove('sung', 'active', 'upcoming')
        el.classList.add(state)

        // Update sweep progress for active words
        if (state === 'active') {
          el.style.setProperty('--sweep-progress', `${progress}%`)
          el.style.setProperty('--glow-opacity', glowOpacity)
        }
      })

      rafIdRef.current = requestAnimationFrame(animate)
    }

    rafIdRef.current = requestAnimationFrame(animate)

    return () => {
      isRunning = false
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
      }
    }
  }, [isCurrent, segments, isPlaying, calculateWordState])

  // For non-current lines, calculate state from props (no animation needed)
  const getWordState = useCallback((word) => {
    const time = isCurrent ? lastInterpolatedTimeRef.current : currentTime
    const { state, progress, glowOpacity } = calculateWordState(word, time)
    return { className: state, progress, glowOpacity }
  }, [currentTime, isCurrent, calculateWordState])

  // Determine line state class
  const lineStateClass = useMemo(() => {
    if (isPast) return 'past'
    if (isCurrent) return 'current'
    return 'future'
  }, [isPast, isCurrent])

  // Clicking the line seeks to its first word. Clicking an individual word
  // stops propagation and seeks to that word instead.
  const handleClick = () => {
    if (onClick && segments?.length > 0) {
      onClick(segments[0].start_time)
    }
  }

  const handleWordClick = (event, word) => {
    event.stopPropagation()
    if (onClick) {
      onClick(word.start_time)
    }
  }

  // Store ref for each word element
  const setWordRef = useCallback((el, idx) => {
    wordRefsRef.current[idx] = el
  }, [])

  if (!segments || segments.length === 0) {
    return null
  }

  return (
    <div
      className={`karaoke-line ${lineStateClass} cursor-pointer`}
      onClick={handleClick}
      style={{ textShadow: isCurrent ? '0 0 20px rgba(255,255,255,0.3)' : 'none' }}
    >
      <div className="w-full">
        {/* Primary lyrics - word by word with sweep animation */}
        {showLyrics && (
          <div className="flex flex-wrap justify-center gap-1" style={{ fontSize, lineHeight: 1.3 }}>
            {segments.map((word, idx) => {
              const { className, progress, glowOpacity } = getWordState(word)
              return (
                <span
                  key={idx}
                  ref={(el) => setWordRef(el, idx)}
                  className={`karaoke-word ${className}`}
                  style={className === 'active' ? {
                    '--sweep-progress': `${progress}%`,
                    '--glow-opacity': glowOpacity
                  } : undefined}
                  onClick={(event) => handleWordClick(event, word)}
                >
                  {word.text}
                </span>
              )
            })}
          </div>
        )}

        {/* Romanization (shown at line level) */}
        {showRomanization && romanization && (
          <div className="karaoke-secondary mt-1">
            {romanization}
          </div>
        )}

        {/* Translation (shown at line level) */}
        {showTranslation && translation && (
          <div className="karaoke-secondary mt-1">
            {translation}
          </div>
        )}
      </div>
    </div>
  )
})

WordLevelLine.displayName = 'WordLevelLine'

export default WordLevelLine
